/**
 * @module FieldControls
 */
import { createStyles, FormHelperText, makeStyles, Tooltip, Typography } from '@material-ui/core';
import { DateTimePicker, DatePicker as MUIDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { deepMerge } from '@sensenet/client-utils';
import { DateTimeMode } from '@sensenet/default-content-types';
import DateFnsUtils from '@date-io/date-fns';
import intlFormatDistance from 'date-fns/intlFormatDistance';
import React, { useState } from 'react';
import { changeTemplatedValue } from "../helpers.js";
import { defaultLocalization } from "./localization.js";
const minDatePickerDate = new Date('0001-01-01');
export const dateTimeOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit'
};
const useStyles = makeStyles(theme => {
  return createStyles({
    textDate: {
      color: `hsl(174deg 3% ${theme.palette.type === 'light' ? '41' : '74'}%)`,
      fontSize: '0.66rem',
      letterSpacing: '0.5px',
      marginLeft: '5px',
      verticalAlign: 'middle'
    }
  });
});
const initialValueState = ({
  fieldValue,
  actionName,
  settings
}) => {
  if (fieldValue === '0001-01-01T00:00:00Z') {
    return null;
  }
  if (fieldValue) {
    return fieldValue;
  }
  if (actionName !== 'new') {
    return null;
  }
  const secureCheckedDateInput = changeTemplatedValue(settings.DefaultValue, settings.EvaluatedDefaultValue);
  return secureCheckedDateInput;
};
/**
 * Field control that represents a Date field. Available values will be populated from the FieldSettings.
 */
export const DatePicker = props => {
  const classes = useStyles();
  const {
    settings,
    actionName,
    fieldValue,
    locale,
    localization,
    hideDescription,
    fieldOnChange
  } = props;
  const localizationMerged = deepMerge(defaultLocalization.datePicker, localization === null || localization === void 0 ? void 0 : localization.datePicker);
  const [value, setValue] = useState(initialValueState({
    fieldValue,
    actionName,
    settings
  }));
  const handleDateChange = date => {
    if (!date) {
      return;
    }
    setValue(new Date(date).toISOString());
    fieldOnChange === null || fieldOnChange === void 0 ? void 0 : fieldOnChange(settings.Name, new Date(date).toISOString());
  };
  const localeCode = (locale === null || locale === void 0 ? void 0 : locale.code) || window.navigator.language;
  const dateFieldValue = new Date(fieldValue);
  switch (actionName) {
    case 'edit':
    case 'new':
      return /*#__PURE__*/React.createElement(MuiPickersUtilsProvider, {
        utils: DateFnsUtils,
        locale: locale
      }, /*#__PURE__*/React.createElement(React.Fragment, null, settings.DateTimeMode === DateTimeMode.Date ? /*#__PURE__*/React.createElement(MUIDatePicker, {
        style: {
          display: 'inherit'
        },
        value: value,
        minDate: minDatePickerDate,
        onChange: handleDateChange,
        name: settings.Name,
        label: settings.DisplayName,
        id: settings.Name,
        disabled: settings.ReadOnly,
        InputLabelProps: {
          shrink: true
        },
        required: settings.Compulsory,
        format: "yyyy MMMM dd"
      }) : /*#__PURE__*/React.createElement(DateTimePicker, {
        style: {
          display: 'inherit'
        },
        minDate: minDatePickerDate,
        value: value,
        onChange: handleDateChange,
        label: settings.DisplayName,
        name: settings.Name,
        id: settings.Name,
        disabled: settings.ReadOnly,
        InputLabelProps: {
          shrink: true
        },
        required: settings.Compulsory,
        format: "yyyy MMMM dd hh:mm aaaa",
        InputProps: {
          readOnly: true,
          style: {
            minWidth: '200px',
            width: '100%'
          }
        }
      }), !hideDescription && /*#__PURE__*/React.createElement(FormHelperText, null, settings.Description)));
    default:
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
        variant: "caption",
        gutterBottom: true
      }, settings.DisplayName, settings.DateTimeMode === DateTimeMode.DateAndTime && fieldValue && /*#__PURE__*/React.createElement("span", {
        className: classes.textDate
      }, intlFormatDistance(dateFieldValue, new Date(), {
        locale: localeCode
      }))), /*#__PURE__*/React.createElement(Tooltip, {
        title: fieldValue
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "body1",
        gutterBottom: true
      }, fieldValue ? settings.DateTimeMode === DateTimeMode.Date ? new Intl.DateTimeFormat(localeCode).format(dateFieldValue) : new Intl.DateTimeFormat(localeCode, dateTimeOptions).format(dateFieldValue) : localizationMerged.noValue)));
  }
};
//# sourceMappingURL=date-picker.js.map